import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brandIntelApi } from '@/lib/api';
import { OnboardingProfile } from '@/types';
import { queryKeys } from './shared';

// Brand Intelligence
export const useUpdateOnboarding = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, data }: { brandId: string; data: OnboardingProfile }) =>
      brandIntelApi.updateOnboarding(workspaceId, brandId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
    },
  });
};

export const useTriggerCrawl = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) => brandIntelApi.triggerCrawl(workspaceId, brandId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
    },
  });
};

export const useBrandKnowledge = (workspaceId: string, brandId: string) =>
  useQuery({
    queryKey: queryKeys.brandKnowledge(workspaceId, brandId),
    queryFn: () => brandIntelApi.getKnowledge(workspaceId, brandId),
    enabled: !!workspaceId && !!brandId,
  });

/**
 * Per-platform RAG memory: themes, style, recent topics — what Merkflow has
 * "learned" from this brand's published posts. Stored in `brand_post_memory`
 * and refreshed nightly by `PostMemoryService.updatePostMemorySummary`.
 * Backend route: `GET /api/workspaces/:id/brands/:brandId/post-memory`.
 */
export const useBrandPostMemory = (workspaceId: string, brandId: string) =>
  useQuery({
    queryKey: ['brand-post-memory', workspaceId, brandId],
    queryFn: () => brandIntelApi.getPostMemory(workspaceId, brandId),
    enabled: !!workspaceId && !!brandId,
    staleTime: 60_000,
  });

// Tier 3.9 — brand voice maturity widget. 5-min staleTime since the
// underlying data only moves on publish events / nightly memory refresh.
export const useVoiceMaturity = (workspaceId: string, brandId: string) =>
  useQuery({
    queryKey: ['voice-maturity', workspaceId, brandId],
    queryFn: () => brandIntelApi.getVoiceMaturity(workspaceId, brandId),
    enabled: !!workspaceId && !!brandId,
    staleTime: 5 * 60_000,
  });
